import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  styled,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  HighlightOffOutlined,
  Image,
  VideoFile,
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  Search,
} from '@mui/icons-material';
import { useListMediaQuery, useListFoldersQuery } from 'src/backend';
import { PresetMediaItem } from 'src/pages/MachineGroupDetails/UpsertPresetForm';
import { useState, useMemo } from 'react';

const MediaTile = styled(Card)(({ theme }) => ({
  position: 'relative',
  aspectRatio: '1',
  width: '120px',
  height: '120px',
  minWidth: '120px',
  minHeight: '120px',
  maxWidth: '120px',
  maxHeight: '120px',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',
  flexShrink: 0,
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const MediaPreview = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '80px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[100],
  overflow: 'hidden',
}));

export type FolderItem = {
  id: string;
  name: string;
  mediaCount: number;
};

interface MediaSelectionDialogProps {
  isOpen: boolean;
  specificCustomerId: string;
  onClose: () => void;
  onSelectMedia: (media: PresetMediaItem) => void;
  selectedMediaId?: string;
}

function MediaSelectionDialog({
  isOpen,
  specificCustomerId,
  onClose,
  onSelectMedia,
  selectedMediaId,
}: MediaSelectionDialogProps) {
  const { data: mediaData, isLoading } = useListMediaQuery(specificCustomerId);
  const { data: foldersData } = useListFoldersQuery(specificCustomerId);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [searchText, setSearchText] = useState<string>('');

  // Group media by folders
  const groupedMedia = useMemo(() => {
    if (!mediaData?.data) return { folders: [], mediaWithoutFolder: [] };

    // Filter media by search text first
    const filteredData = searchText
      ? mediaData.data.filter((media) =>
          media.fileName.toLowerCase().includes(searchText.toLowerCase()),
        )
      : mediaData.data;

    const mediaByFolder: { [key: string]: PresetMediaItem[] } = {};
    const mediaWithoutFolder: PresetMediaItem[] = [];

    filteredData.forEach((media) => {
      if (media.folderId) {
        if (!mediaByFolder[media.folderId]) {
          mediaByFolder[media.folderId] = [];
        }
        mediaByFolder[media.folderId].push(media);
      } else {
        mediaWithoutFolder.push(media);
      }
    });

    // Create folder objects with media, but only show folders that have matching media when searching
    const folders =
      foldersData?.data
        ?.map((folder) => ({
          ...folder,
          media: mediaByFolder[folder.id] || [],
        }))
        .filter((folder) => !searchText || folder.media.length > 0) || [];

    return { folders, mediaWithoutFolder };
  }, [mediaData?.data, foldersData?.data, searchText]);

  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const renderMediaPreview = (media: PresetMediaItem) => {
    const { fileName } = media;
    const isVideo =
      media.fullFileUrl.toLowerCase().includes('.mp4') ||
      fileName.toLowerCase().endsWith('.mp4');
    const isImage =
      media.fullFileUrl.toLowerCase().includes('.jpg') ||
      media.fullFileUrl.toLowerCase().includes('.jpeg') ||
      fileName.toLowerCase().endsWith('.jpg') ||
      fileName.toLowerCase().endsWith('.jpeg');

    if (media.thumbnailUrl) {
      return (
        <CardMedia
          component="img"
          src={media.thumbnailUrl}
          alt={fileName}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: 'grey.50',
          }}
        />
      );
    }
    if (isVideo) {
      return <VideoFile sx={{ fontSize: 32, color: 'grey.500' }} />;
    }
    if (isImage) {
      return <Image sx={{ fontSize: 32, color: 'grey.500' }} />;
    }
    return <Image sx={{ fontSize: 32, color: 'grey.500' }} />;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          height: '600px',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle>
        Select Media
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <HighlightOffOutlined />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 1 }}
        >
          <TextField
            name="search"
            label="Search media"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            sx={{ minWidth: 200, flex: 1 }}
            placeholder="Search by filename..."
          />
          <IconButton onClick={() => setSearchText('')} disabled={!searchText}>
            <Tooltip title="Clear search">
              <HighlightOffOutlined />
            </Tooltip>
          </IconButton>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading media...</Typography>
          </Box>
        )}

        {!isLoading && mediaData?.data && mediaData.data.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {searchText && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {groupedMedia.folders.reduce(
                  (acc, folder) => acc + folder.media.length,
                  0,
                ) +
                  groupedMedia.mediaWithoutFolder.length ===
                0
                  ? `No results found for "${searchText}"`
                  : `Showing results for "${searchText}"`}
              </Typography>
            )}

            {searchText &&
              groupedMedia.folders.reduce(
                (acc, folder) => acc + folder.media.length,
                0,
              ) +
                groupedMedia.mediaWithoutFolder.length ===
                0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                    textAlign: 'center',
                  }}
                >
                  <Search sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No media found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your search terms
                  </Typography>
                </Box>
              )}

            {groupedMedia.folders.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Folders
                </Typography>
                {groupedMedia.folders.map((folder) => (
                  <Accordion
                    key={folder.id}
                    expanded={expandedFolders.has(folder.id)}
                    onChange={() => toggleFolder(folder.id)}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                        }}
                      >
                        <FolderIcon sx={{ color: 'primary.main' }} />
                        <Typography variant="h6">{folder.name}</Typography>
                        <Chip
                          label={`${folder.media.length} files`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {folder.media.length > 0 ? (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                          }}
                        >
                          {folder.media.map((media) => (
                            <MediaTile
                              key={media.id}
                              onClick={() => onSelectMedia(media)}
                              sx={{
                                border: selectedMediaId === media.id ? 2 : 0,
                                borderColor:
                                  selectedMediaId === media.id
                                    ? 'primary.main'
                                    : 'transparent',
                              }}
                            >
                              <MediaPreview>
                                {renderMediaPreview(media)}
                              </MediaPreview>
                              <CardContent
                                sx={{ p: 1, '&:last-child': { pb: 1 } }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                  }}
                                >
                                  {media.fileName}
                                </Typography>
                              </CardContent>
                            </MediaTile>
                          ))}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 100,
                            textAlign: 'center',
                          }}
                        >
                          <FolderIcon
                            sx={{ fontSize: 48, color: 'grey.400', mb: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            No media files in this folder
                          </Typography>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}

            {/* Media without folder */}
            {groupedMedia.mediaWithoutFolder.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Other Media
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    p: 2,
                    justifyContent: 'flex-start',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  {groupedMedia.mediaWithoutFolder.map((media) => (
                    <MediaTile
                      key={media.id}
                      onClick={() => onSelectMedia(media)}
                      sx={{
                        border: selectedMediaId === media.id ? 2 : 0,
                        borderColor:
                          selectedMediaId === media.id
                            ? 'primary.main'
                            : 'transparent',
                      }}
                    >
                      <MediaPreview>{renderMediaPreview(media)}</MediaPreview>
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Typography
                          variant="caption"
                          sx={{
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                        >
                          {media.fileName}
                        </Typography>
                      </CardContent>
                    </MediaTile>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {!isLoading && (!mediaData?.data || mediaData.data.length === 0) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>No media files found</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

export default MediaSelectionDialog;
